import React, { memo, useMemo, CSSProperties, useCallback, useState, Fragment } from 'react';
import { PopperAnchor, Popper } from '@core/components/Popper';
import { CardTitle, Collapse } from 'reactstrap';
import { toMoment } from '@core/utils/time';
import ScrollBar from 'react-perfect-scrollbar';
import { WithTooltip } from '@modules/common';
import { CalendarService, CommonService, HTMLService } from '@business/services';
import { R } from '@core/utils/r';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { EventReminder, GCCalendarEventObjectType, GCCalendarEventResponse } from '@business/entities/gc_calendar';
import { OrgId } from '@business/entities/organization';
import { openDealOverlay } from '@modules/deals/DealOverlay';
import { openTimeMaterialOverlay } from '@modules/timeMaterials/TimeMaterialOverlay';
import { openProjectOverlay } from '@modules/projects/ProjectOverlay';

interface JustEventProps {
  event: GCCalendarEventResponse;
}

const RowBase = memo((props: { children: React.ReactNode; icon: string; iconClass?: string; iconStyle?: CSSProperties; centerIcon?: boolean }) => {
  return (
    <div className={`d-flex align-items-${props.centerIcon ? 'center' : 'start'}`} style={{ paddingLeft: 20, marginBottom: 15 }}>
      <div style={{ width: 40, textAlign: 'center', fontSize: 16, paddingRight: 20, ...props.iconStyle }} className={props.iconClass}>
        <i className={props.icon}></i>
      </div>
      <div className="flex-fill">{props.children}</div>
    </div>
  );
});

const getEventStatusBadge = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'accepted':
      return <span className="badge badge-success ml-2">Accepted</span>;
    case 'declined':
      return <span className="badge badge-danger ml-2">Declined</span>;
    case 'rescheduled':
      return <span className="badge badge-warning ml-2">Rescheduled</span>;
    case 'out_of_office':
      return <span className="badge badge-secondary ml-2">Out of office</span>;
    case 'maybe':
    case 'tentative':
      return <span className="badge badge-warning ml-2">Maybe</span>;
    default:
      return null;
  }
};

const EventHeader = memo((props: JustEventProps) => {
  const { event } = props;
  const startTime = useMemo(() => toMoment(event.startTime), [event]);
  const endTime = useMemo(() => toMoment(event.endTime), [event]);
  const isSameDay = useMemo(() => startTime.isSame(endTime, 'day'), [startTime, endTime]);

  const sameMeridiem = startTime.format('a') === endTime.format('a');
  const time = useMemo(() => {
    if (isSameDay) {
      return `${startTime.format('dddd, MMMM D · h:mm' + (!sameMeridiem ? 'a' : ''))} － ${endTime.format('h:mma')}`;
    }
    return `${startTime.format('MMMM d, YYYY,  h:mm' + (!sameMeridiem ? 'a' : ''))} － ${endTime.format('MMM d, YYYY, h:mma')}`;
  }, [isSameDay, startTime, sameMeridiem, endTime]);

  return (
    <Fragment>
      <RowBase icon="fa fa-square" iconClass="text-primary" iconStyle={{ marginTop: 5 }}>
        <div className="d-flex align-items-center">
          <CardTitle style={{ fontSize: 20, marginBottom: 0 }}>{event.name}</CardTitle>
          {event.status && getEventStatusBadge(event.status)}
        </div>
        <div className="text-muted mt-1">{time}</div>
      </RowBase>
      {!R.isEmpty(event.location) && (
        <RowBase icon="fa fa-map-marker-alt" iconClass="text-muted" centerIcon>
          <div>{event.location}</div>
        </RowBase>
      )}
    </Fragment>
  );
});

const Guests = memo((props: JustEventProps) => {
  const { event } = props;

  const MAX_LENGTH = 7;
  const moreParticipants = useMemo(() => event.participants.length > MAX_LENGTH, [event]);
  const [isOpen, setOpen] = useState(!moreParticipants);
  const yesParticipants = useMemo(() => event.participants.filter(p => p.acceptanceStatus === 'yes'), [event]);
  const noParticipants = useMemo(() => event.participants.filter(p => p.acceptanceStatus === 'no'), [event]);
  const waitingParticipants = useMemo(() => event.participants.filter(p => p.acceptanceStatus !== 'yes' && p.acceptanceStatus != 'no'), [event]);

  // Separate system users from guest emails
  const systemUsers = useMemo(() => event.participants.filter(p => p.userId && p.user), [event]);
  const guestEmails = useMemo(() => event.participants.filter(p => p.email && !p.userId), [event]);

  //   const getStatusIcon = useCallback((status: string) => {
  //     if (status === 'yes') {
  //       return <i className="font-size-14 mr-2 fa fa-check-circle text-success"></i>;
  //     } else if (status === 'no') {
  //       return <i className="font-size-14 mr-2 fa fa-times-circle text-danger"></i>;
  //     } else {
  //       return <i className="font-size-14 mr-2 fa fa-question-circle text-muted"></i>;
  //     }
  //   }, []);

  return (
    <RowBase icon="fa fa-users" iconClass="text-muted">
      {moreParticipants && (
        <div onClick={() => setOpen(open => !open)} style={{ cursor: 'pointer', marginBottom: 10 }}>
          <div>{event.participants.length} guests</div>
          <div className="text-muted">{yesParticipants.length} yes</div>
          <div className="text-muted">
            {noParticipants.length > 0 && <span>{noParticipants.length} no </span>}
            {waitingParticipants.length > 0 && <span>{waitingParticipants.length} waiting</span>}
          </div>
        </div>
      )}
      <Collapse isOpen={isOpen}>
        {/* System Users Section */}
        {systemUsers.length > 0 && (
          <div>
            {systemUsers.map((p, index) => (
              <div key={`user-${index}`} className="mb-2 d-flex align-items-center">
                <i className={`font-size-14 mr-2 fa ${CalendarService.getStatusIcon(p.acceptanceStatus)}`}></i>
                <i className="fa fa-user-circle text-primary mr-2" style={{ fontSize: 16 }}></i>
                <span className="font-weight-medium">{CommonService.getFullName(p.user)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Guest Emails Section */}
        {guestEmails.length > 0 && (
          <div className={systemUsers.length > 0 ? 'mt-3 pt-2 border-top' : ''}>
            {systemUsers.length > 0 && (
              <div className="text-muted mb-2" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Guest Emails
              </div>
            )}
            {guestEmails.map((p, index) => (
              <div key={`email-${index}`} className="mb-2 d-flex align-items-center">
                <i className={`font-size-14 mr-2 fa ${CalendarService.getStatusIcon(p.acceptanceStatus)}`}></i>
                <i className="fa fa-envelope text-secondary mr-2" style={{ fontSize: 14 }}></i>
                <span className="text-muted" style={{ fontSize: 14 }}>
                  {p.email}
                </span>
              </div>
            ))}
          </div>
        )}
      </Collapse>
    </RowBase>
  );
});

const unitLabel = (unit: string, value: number) => {
  const map: Record<string, string> = { minutes: 'min', hours: 'hr', days: 'day', weeks: 'week' };
  const short = map[unit] || unit;
  return value === 1 ? short : `${short}s`;
};

const Reminders = memo((props: JustEventProps) => {
  const reminders: EventReminder[] = props.event.reminders || [];
  if (reminders.length === 0) return null;

  return (
    <RowBase icon="fa fa-bell" iconClass="text-muted" centerIcon={reminders.length === 1}>
      {reminders.map((r, i) => (
        <div key={i} className="mb-1" style={{ fontSize: 14, color: '#555' }}>
          {r.value} {unitLabel(r.unit, r.value)} before
        </div>
      ))}
    </RowBase>
  );
});

// const EventFooter = memo((props: JustEventProps) => {
//   const { event } = props;
//   const control = useMemo(() => formControl<string>({}), []);

//   return (
//     <div className="border-top d-flex justify-content-end pt-2">
//       <FormPills
//         control={control}
//         items={CommonService.selectItems(
//           ['Yes', 'No', 'Maybe'],
//           f => f,
//           f => {
//             if (f === 'Yes') return 'confirmed';
//             if (f === 'No') return 'cancelled';
//             return 'tentative';
//           },
//         )}
//       />
//     </div>
//   );
// });

export interface EventPopperProps extends JustEventProps {
  anchor: PopperAnchor<HTMLElement>;
  onDelete(eventId: string): void;
  onEdit(event: GCCalendarEventResponse, jsEvent: any): void;
  onAddToGoogle?(eventId: string): void;
  isMyEvent: boolean;
}

export const EventPopper = memo((props: EventPopperProps) => {
  const { anchor, event, isMyEvent, onAddToGoogle } = props;

  const buttons = (
    <Fragment>
      {event.localEventId && props.onEdit && (
        <IconButton
          className="close_button"
          size="small"
          onClick={jsEvent => {
            anchor.onClose();
            props.onEdit(event, jsEvent);
          }}
        >
          <EditIcon />
        </IconButton>
      )}

      {event.localEventId && props.onDelete && (
        <IconButton
          className="close_button"
          size="small"
          onClick={() => {
            anchor.onClose();
            props.onDelete(event.localEventId);
          }}
        >
          <DeleteIcon />
        </IconButton>
      )}

      <IconButton
        className="close_button"
        size="small"
        onClick={() => {
          anchor.onClose();
        }}
      >
        <CloseIcon />
      </IconButton>
    </Fragment>
  );

  const description = useMemo(() => {
    if (event.description && event.description.includes('https://app.grandcentr.al/time-materials/detail')) {
      return event.description.replace(/(https:\/\/app.grandcentr.al\/time-materials\/detail\/\d+)/g, '<a href="$1" target="_blank">$1</a>');
    }
    return event.description;
  }, [event]);

  const onObjectClick = useCallback(() => {
    if (event.objectType && event.objectId) {
      anchor.onClose();
      if (event.objectType === GCCalendarEventObjectType.Deal) {
        openDealOverlay({
          dealId: event.objectId,
          orgId: event.orgId as OrgId,
        });
      } else if (event.objectType === GCCalendarEventObjectType.TimeMaterial) {
        openTimeMaterialOverlay({
          orderId: event.objectId,
        });
      } else if (event.objectType === GCCalendarEventObjectType.Project) {
        openProjectOverlay({
          projectId: event.objectId,
          projectTitle: event.objectName || '',
        });
      }
    }
  }, [event, anchor]);

  return (
    <Popper anchor={anchor} placement="left" autoClose="outside" aboveModal>
      <div className="poper-modal no-drag" style={{ width: 500, paddingLeft: 0 }}>
        <div className="border-bottom d-flex justify-content-end pb-2 mb-2">{buttons}</div>
        <div style={{ padding: 20 }}>
          <ScrollBar>
            <EventHeader event={event} />
            {event.participants.length > 0 && <Guests event={event} />}
            {event.objectId && event.objectName && (
              <RowBase icon="fa fa-link" iconClass="text-muted" centerIcon>
                <a onClick={onObjectClick} className="text-primary">
                  <span>{event.objectName}</span>
                  <p>
                    <small className="text-muted">{CalendarService.getCalendarObjectLabel(event.objectType)}</small>
                  </p>
                </a>
              </RowBase>
            )}
            {/* <RowBase icon="fa fa-calendar-alt" iconClass="text-muted">
              <WithTooltip tooltip={event.createdBy.email}>
                <div>{CommonService.getFullName(event.createdBy) || event.createdBy.email}</div>
              </WithTooltip>
            </RowBase> */}
            {!!event.description && (
              <RowBase icon="">
                <div style={{ maxHeight: 200, overflow: 'auto' }}>{HTMLService.toReact(description)}</div>
              </RowBase>
            )}

            <Reminders event={event} />
            <RowBase icon="">
              {!event.isSynced && onAddToGoogle && (
                <button className="btn btn-sm btn-outline-primary" onClick={() => onAddToGoogle!(event.id)}>
                  Add to Google
                </button>
              )}
              {!!event.isSynced && <span className="badge badge-success">Added to Google Calendar</span>}
            </RowBase>
          </ScrollBar>
          {/* {isMyEvent && <EventFooter event={event} />} */}
        </div>
      </div>
    </Popper>
  );
});
